import React, { useState } from 'react'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'

// Malawi runs on CAT all year (no daylight saving).
export const MALAWI_OFFSET = '+02:00'
const OFFSET_MS = 2 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

export const PERIODS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 }
]

export const GOOD = '#0F7B3F'
export const GOOD_BG = '#E6F4EC'
export const BAD = '#C62828'
export const BAD_BG = '#FDECEC'
const INK = '#0B1F2A'
const INK_MUTED = '#5F6B76'
const LINE = '#E3E8EE'
const CAME_IN = '#B8C2CC'
const PROCESSED = '#032A37'

function isoDay(ms) {
  return new Date(ms).toISOString().slice(0, 10)
}

// The whole Malawi calendar days covering the last `days` days, today included.
export function periodRange(days, nowMs) {
  const local = new Date((nowMs || Date.now()) + OFFSET_MS)
  const todayStart = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    local.getUTCDate()
  )
  const startDay = todayStart - (days - 1) * DAY_MS
  const dates = []
  for (let d = startDay; d <= todayStart; d += DAY_MS) {
    dates.push(isoDay(d))
  }
  return {
    from: new Date(startDay - OFFSET_MS).toISOString(),
    to: new Date(todayStart + DAY_MS - OFFSET_MS - 1).toISOString(),
    dates
  }
}

// Green only when MORE than half was processed, as OMM asked.
export function isOnTrack(performance) {
  return performance !== null && performance !== undefined && performance > 50
}

export function formatPercent(performance) {
  if (performance === null || performance === undefined) {
    return '—'
  }
  return `${Math.round(performance)}%`
}

export function PerformanceBadge(props) {
  const { performance, size } = props
  const hasValue = performance !== null && performance !== undefined
  const good = isOnTrack(performance)
  const small = size === 'small'
  return (
    <Box
      component="span"
      aria-label={
        hasValue
          ? `${formatPercent(performance)} processed, ${
              good ? 'on track' : 'behind'
            }`
          : 'No arrivals in this period'
      }
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        ml: small ? 1 : 0,
        px: small ? '6px' : '10px',
        py: small ? '1px' : '4px',
        borderRadius: '999px',
        fontSize: small ? '11px' : '13px',
        fontWeight: 700,
        lineHeight: small ? '16px' : '18px',
        fontVariantNumeric: 'tabular-nums',
        color: hasValue ? (good ? GOOD : BAD) : INK_MUTED,
        background: hasValue ? (good ? GOOD_BG : BAD_BG) : '#F1F3F5'
      }}
    >
      {hasValue ? <span aria-hidden="true">{good ? '▲' : '▼'}</span> : null}
      {formatPercent(performance)}
    </Box>
  )
}

function Stat(props) {
  return (
    <Box sx={{ minWidth: 76 }}>
      <Box
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: INK_MUTED
        }}
      >
        {props.label}
      </Box>
      <Box
        sx={{
          mt: '2px',
          fontSize: '26px',
          fontWeight: 700,
          lineHeight: '32px',
          color: INK,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {props.children}
      </Box>
    </Box>
  )
}

function LegendSwatch(props) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '2px',
          background: props.color
        }}
      />
      {props.label}
    </Box>
  )
}

// Daily came-in vs processed, two thin bars per day on one shared axis.
export function DailyBars(props) {
  const { dates, daily, processedLabel } = props
  const byDate = {}
  ;(daily || []).forEach(function(day) {
    byDate[day.date] = day
  })
  const days = dates.map(function(date) {
    const day = byDate[date] || { came_in: 0, processed: 0 }
    return { date, came_in: day.came_in, processed: day.processed }
  })
  const max = Math.max.apply(
    null,
    [1].concat(
      days.map(function(d) {
        return d.came_in
      })
    )
  )
  const height = 44

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box
        role="img"
        aria-label={`Daily arrivals and ${processedLabel.toLowerCase()}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: days.length > 10 ? '2px' : '6px',
          height,
          borderBottom: `1px solid ${LINE}`
        }}
      >
        {days.map(function(d) {
          const pct =
            d.came_in > 0 ? Math.round((d.processed / d.came_in) * 100) : null
          return (
            <Tooltip
              key={d.date}
              arrow
              placement="top"
              title={
                <span>
                  <strong>{d.date}</strong>
                  <br />
                  Came in: {d.came_in}
                  <br />
                  {processedLabel}: {d.processed}
                  {pct !== null ? ` (${pct}%)` : ''}
                </span>
              }
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: '2px',
                  height: '100%',
                  cursor: 'default',
                  '&:hover': { background: '#F5F7F9' }
                }}
              >
                <Box
                  sx={{
                    width: '45%',
                    maxWidth: 8,
                    height: `${(d.came_in / max) * 100}%`,
                    minHeight: d.came_in ? 2 : 0,
                    background: CAME_IN,
                    borderRadius: '2px 2px 0 0'
                  }}
                />
                <Box
                  sx={{
                    width: '45%',
                    maxWidth: 8,
                    height: `${(d.processed / max) * 100}%`,
                    minHeight: d.processed ? 2 : 0,
                    background: PROCESSED,
                    borderRadius: '2px 2px 0 0'
                  }}
                />
              </Box>
            </Tooltip>
          )
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: '6px',
          fontSize: '11px',
          color: INK_MUTED
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <LegendSwatch color={CAME_IN} label="Came in" />
          <LegendSwatch color={PROCESSED} label={processedLabel} />
        </Box>
        <span>
          {dates[0].slice(5)} → {dates[dates.length - 1].slice(5)}
        </span>
      </Box>
    </Box>
  )
}

export function PeriodToggle(props) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={props.value}
      onChange={function(event, value) {
        if (value) props.onChange(value)
      }}
      aria-label="Metrics period"
      sx={{
        background: '#F1F3F5',
        borderRadius: '8px',
        p: '2px',
        '& .MuiToggleButton-root': {
          border: 0,
          borderRadius: '6px !important',
          px: 1.25,
          py: '2px',
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'none',
          color: INK_MUTED
        },
        '& .Mui-selected': {
          background: '#FFFFFF !important',
          color: `${INK} !important`,
          boxShadow: '0 1px 2px rgba(11, 31, 42, 0.12)'
        }
      }}
    >
      {PERIODS.map(function(period) {
        return (
          <ToggleButton key={period.key} value={period.key}>
            {period.label}
          </ToggleButton>
        )
      })}
    </ToggleButtonGroup>
  )
}

// Props: basket ({ came_in, processed, performance, daily } or null),
// basketLabel, processedLabel, note, period, onPeriodChange, dates, loading,
// error.
export default function DeskMetrics(props) {
  const [showInfo, setShowInfo] = useState(false)
  const basket = props.basket
  const processedLabel = props.processedLabel || 'Processed'

  return (
    <Box
      data-testid="desk-metrics"
      sx={{
        border: `1px solid ${LINE}`,
        borderRadius: '12px',
        p: 2,
        minWidth: 300,
        maxWidth: 420,
        width: '100%',
        background: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(11, 31, 42, 0.06)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: INK,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {props.basketLabel}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={function() {
              setShowInfo(!showInfo)
            }}
            sx={{
              p: 0,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              fontSize: '11px',
              color: INK_MUTED,
              textDecoration: 'underline dotted'
            }}
          >
            How is this measured?
          </Box>
        </Box>
        <PeriodToggle value={props.period} onChange={props.onPeriodChange} />
      </Box>

      {showInfo ? (
        <Box
          sx={{
            mb: 1.5,
            p: 1,
            borderRadius: '8px',
            background: '#F5F7F9',
            fontSize: '11px',
            lineHeight: '16px',
            color: INK_MUTED
          }}
        >
          <strong>Came in</strong>: policies that entered this basket in the
          period. <strong>{processedLabel}</strong>: of those, how many have
          left it since. Green when more than half has been{' '}
          {processedLabel.toLowerCase()}.{props.note ? ` ${props.note}` : ''}
        </Box>
      ) : null}

      {props.error ? (
        <Box sx={{ fontSize: '12px', color: BAD }}>
          Could not load metrics. {props.error}
        </Box>
      ) : props.loading && !basket ? (
        <Box>
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={44} sx={{ mt: 1.5 }} />
        </Box>
      ) : basket ? (
        <Box
          sx={{ opacity: props.loading ? 0.5 : 1, transition: 'opacity 150ms' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <Stat label="Came in">{basket.came_in}</Stat>
            <Stat label={processedLabel}>{basket.processed}</Stat>
            <Box sx={{ textAlign: 'right' }}>
              <Box
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: INK_MUTED,
                  mb: '6px'
                }}
              >
                Performance
              </Box>
              <PerformanceBadge performance={basket.performance} />
            </Box>
          </Box>
          {props.dates && props.dates.length > 1 ? (
            <DailyBars
              dates={props.dates}
              daily={basket.daily}
              processedLabel={processedLabel}
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}
